/**
 * Unified Wallet Hook
 *
 * Real, Demo, Copy, Bot 모든 지갑을 한 번에 조회하는 SWR Hook
 *
 * ⚠️ IMPORTANT: 신규 API (port 4333) 사용
 *
 * @example
 * ```typescript
 * const { data, isLoading, error, mutate } = useUnifiedWallet();
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error loading wallets</div>;
 *
 * // 각 지갑 정보 접근
 * const realSpotBalance = data?.real.spot.balance;
 * const demoBalance = data?.demo.balance;
 * const copyBalance = data?.copy.balance;
 * const botBalance = data?.bot.balance;
 * ```
 */

import { unifiedWalletApi, type UnifiedWalletResponse } from '@app/api/unifiedWallet';
import useSWR from 'swr';
import { useToken } from '@app/hooks/auth/token';

const UNIFIED_WALLET_KEY = '/wallet/unified';

/**
 * 통합 지갑 조회 Hook
 *
 * @param options - SWR 옵션
 * @returns SWR 응답 (data, isLoading, error, mutate)
 */
export function useUnifiedWallet(options?: {
  refreshInterval?: number;
  revalidateOnFocus?: boolean;
}) {
  const { accessToken } = useToken();

  // 비로그인 상태면 API 호출 스킵 (key를 null로 설정)
  const { data, error, mutate, isLoading } = useSWR<UnifiedWalletResponse>(
    accessToken ? UNIFIED_WALLET_KEY : null,
    async () => {
      const response = await unifiedWalletApi.getAll();
      return response;
    },
    {
      refreshInterval: options?.refreshInterval || 10000, // 기본: 10초마다 갱신
      revalidateOnFocus: options?.revalidateOnFocus ?? true, // 기본: 포커스 시 재검증
      dedupingInterval: 2000, // 2초 동안 중복 요청 방지
      shouldRetryOnError: false, // 에러 시 재시도 안 함
      ...options,
    },
  );

  return {
    data: data?.data,
    isLoading,
    error,
    mutate,
  };
}

/**
 * Real 지갑만 조회하는 Hook
 */
export function useRealWallets() {
  const { data, isLoading, error, mutate } = useUnifiedWallet();

  return {
    data: data?.real,
    isLoading,
    error,
    mutate,
  };
}

/**
 * Demo 지갑만 조회하는 Hook
 */
export function useDemoWallet() {
  const { data, isLoading, error, mutate } = useUnifiedWallet();

  return {
    data: data?.demo,
    isLoading,
    error,
    mutate,
  };
}

/**
 * Copy 지갑만 조회하는 Hook
 */
export function useCopyWallet() {
  const { data, isLoading, error, mutate } = useUnifiedWallet();

  return {
    data: data?.copy,
    isLoading,
    error,
    mutate,
  };
}

/**
 * Bot 지갑만 조회하는 Hook
 */
export function useBotWallet() {
  const { data, isLoading, error, mutate } = useUnifiedWallet();

  return {
    data: data?.bot,
    isLoading,
    error,
    mutate,
  };
}
